import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header2";
import Button from "../../components/ui/Button";
import PersonalInfoSection from "./components/PersonalInfoSection";
import ContactInfoSection from "./components/ContactInfoSection";
import AddressInfoSection from "./components/AddressInfoSection";
import ProfilePhotoSection from "./components/ProfilePhotoSection";
import Icon from "../../components/AppIcon";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  getProvincesService,
  updateProfileService,
} from "redux/services/general_service";
import axios from "axios";
import * as URLS from "../../../urls";
import { toast } from "react-toastify";

const EditProfile = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { provinces } = useSelector((state) => state.general);
  const user = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user.first_name,
    lastName: user.last_name,
    email: user.email,
    phone: user.phone,
    address: user.address || "",
    province: user.province || "",
  });
  const [errors, setErrors] = useState({});

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasUnsavedChanges(true);

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName?.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName?.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email?.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Please enter a valid email address";
    if (!formData.phone?.trim()) newErrors.phone = "Phone number is required";
    if (!formData.address?.trim()) newErrors.address = "Address is required";
    if (!formData.province) newErrors.province = "Province is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save action
  const handleSave = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${URLS.baseUrl}/${URLS?.profileURL}`,
        { ...formData, "_method": "PUT" },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("Token")}`,
          },
        }
      );

      if (response?.status === 200 || response?.status === 201) {
        toast.success(t("Profile updated successfully!"));
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      if (error.response && error.response.status === 422) {
        const validationErrors = error.response.data.errors;
        if (validationErrors) {
          Object.keys(validationErrors).forEach((key) => {
            validationErrors[key].forEach((message) => {
              toast.error(t(message));
            });
          });
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle cancel action
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowExitConfirmation(true);
    } else {
      navigate(-1);
    }
  };

  // Handle exit confirmation
  const handleExitConfirm = () => {
    setHasUnsavedChanges(false);
    setShowExitConfirmation(false);
    navigate(-1);
  };

  // Handle exit cancel
  const handleExitCancel = () => {
    setShowExitConfirmation(false);
  };

  useEffect(() => {
    dispatch(getProvincesService(1));
  }, [dispatch]);

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <div className="max-w-4xl mx-auto px-4 lg:py-24 py-8">
        {/* Page Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleCancel}
                className="p-2 text-gray-600 hover:text-blue-500 transition"
              >
                <Icon name="ArrowLeft" size={24} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  {t("editTitle")}
                </h1>
                <p className="text-gray-500 mt-1">
                  {t("updateDescription")}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
              >
                {t("cancel")}
              </Button>
              <Button
                onClick={handleSave}
                loading={isLoading}
                disabled={isLoading}
                iconName="Save"
              >
                {t("save")}
              </Button>
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <div className="space-y-6">
          <PersonalInfoSection
            firstName={formData.firstName}
            lastName={formData.lastName}
            onFirstNameChange={(value) => handleFieldChange("firstName", value)}
            onLastNameChange={(value) => handleFieldChange("lastName", value)}
            errors={{
              firstName: errors.firstName,
              lastName: errors.lastName,
            }}
          />

          <ContactInfoSection
            email={formData.email}
            phone={formData.phone}
            onEmailChange={(value) => handleFieldChange("email", value)}
            onPhoneChange={(value) => handleFieldChange("phone", value)}
            errors={{
              email: errors.email,
              phone: errors.phone,
            }}
          />

          <AddressInfoSection
            address={formData.address}
            province={formData.province}
            provinces={provinces}
            onAddressChange={(value) => handleFieldChange("address", value)}
            onProvinceChange={(value) => handleFieldChange("province", value)}
            errors={{
              address: errors.address,
              province: errors.province,
            }}
          />
        </div>

        {/* Mobile Save Button */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
              className="flex-1"
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={handleSave}
              loading={isLoading}
              disabled={isLoading}
              iconName="Save"
              className="flex-1"
            >
              {t("save")}
            </Button>
          </div>
        </div>
      </div>

      {/* Exit Confirmation Modal */}
      {showExitConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-md max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-yellow-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {t("unsavedChanges")}
                </h3>
                <p className="text-gray-500 text-sm">
                  {t("unsavedChangesWarning")}
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={handleExitCancel}
                className="flex-1"
              >
                {t("stay")}
              </Button>
              <Button
                variant="destructive"
                onClick={handleExitConfirm}
                className="flex-1"
              >
                {t("leave")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProfile;
